(() => {
  if (customElements.get('ps-instagram-carousel')) return;
  let instagramScript;
  const loadInstagram = () => {
    if (window.instgrm?.Embeds) return Promise.resolve();
    if (instagramScript) return instagramScript;
    const pending = new Promise((resolve, reject) => {
      let existing = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      if (existing?.dataset.psIgLoader === 'owned' && existing.dataset.psIgTimedOut === 'true') {
        existing.remove();
        existing = null;
      }
      const script = existing || document.createElement('script');
      if (!existing) script.dataset.psIgLoader = 'owned';
      const timer = setTimeout(() => {
        if (script.dataset.psIgLoader === 'owned') script.dataset.psIgTimedOut = 'true';
        reject(new Error('timeout'));
      }, 12000);
      script.addEventListener('load', () => {
        clearTimeout(timer);
        if (window.instgrm?.Embeds) {
          // A slow script may arrive after the timeout. Process the current
          // placeholder too, so an open modal can recover without another click.
          try {
            window.instgrm.Embeds.process();
            resolve();
          } catch {
            reject(new Error('unavailable'));
          }
        } else {
          if (!existing) script.remove();
          reject(new Error('unavailable'));
        }
      }, { once: true });
      script.addEventListener('error', () => {
        clearTimeout(timer);
        if (!existing) script.remove();
        reject(new Error('unavailable'));
      }, { once: true });
      if (!existing) { script.src = 'https://www.instagram.com/embed.js'; script.async = true; document.head.append(script); }
    });
    instagramScript = pending;
    pending.catch(() => { if (instagramScript === pending) instagramScript = undefined; });
    return pending;
  };
  const normalizeInstagramUrl = value => {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || !['www.instagram.com', 'instagram.com'].includes(url.hostname) || url.port || url.username || url.password) return null;
      const match = url.pathname.match(/^\/(reel|p)\/([A-Za-z0-9_-]+)\/?$/);
      return match ? `https://www.instagram.com/${match[1]}/${match[2]}/` : null;
    } catch { return null; }
  };
  class PSInstagramCarousel extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.track = this.querySelector('.ps-ig__track');
      this.cards = [...this.querySelectorAll('.ps-ig__card')];
      this.pagination = this.querySelector('.ps-ig__pagination');
      this.mobile = undefined;
      this.step = 4;
      this.dots = [...this.querySelectorAll('[data-page]')];
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.status = this.querySelector('[data-status]');
      this.dialog = this.querySelector('dialog');
      this.embed = this.querySelector('[data-embed]');
      this.embedStatus = this.querySelector('[data-embed-status]');
      this.index = 0;
      this.previous?.addEventListener('click', () => this.go(this.index - 1), options);
      this.next?.addEventListener('click', () => this.go(this.index + 1), options);
      this.pagination?.addEventListener('click', event => {
        const dot = event.target.closest('[data-page]');
        if (dot) this.go(Number(dot.dataset.page));
      }, options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal: this.abort.signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        const targets = { ArrowRight: this.index + 1, ArrowLeft: this.index - 1, Home: 0, End: this.pageCount - 1 };
        if (event.key in targets) { event.preventDefault(); this.go(targets[event.key]); }
      }, options);
      this.querySelectorAll('[data-reel-url]').forEach(link => {
        const url = normalizeInstagramUrl(link.dataset.reelUrl);
        if (!url) return;
        link.addEventListener('click', event => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.open(link, url);
        }, options);
      });
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.cleanModal(), options);
      this.addEventListener('shopify:block:select', event => {
        const card = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
        if (card) this.go(Math.floor(this.cards.indexOf(card) / this.step));
      }, options);
      this.resize = new ResizeObserver(() => this.syncLayout());
      this.resize.observe(this.track);
      this.syncLayout();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.cleanModal();
    }
    syncLayout() {
      const mobile = matchMedia('(max-width: 749px)').matches;
      if (mobile !== this.mobile) {
        const currentVideo = this.index * this.step;
        this.mobile = mobile;
        this.step = mobile ? 1 : 4;
        this.pageCount = Math.ceil(this.cards.length / this.step);
        this.index = Math.min(this.pageCount - 1, Math.floor(currentVideo / this.step));
        if (this.pagination) {
          this.pagination.replaceChildren();
          for (let index = 0; index < this.pageCount; index++) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'ps-ig__dot';
            button.dataset.page = String(index);
            button.setAttribute('aria-label', this.dataset.goLabel.replace('[number]', String(index + 1)));
            this.pagination.append(button);
          }
          this.dots = [...this.pagination.children];
        }
        this.go(this.index, false);
      }
      this.update();
    }
    go(index, animate = true) {
      const target = this.cards[Math.max(0, Math.min(this.pageCount - 1, index)) * this.step];
      if (!target) return;
      this.track.scrollTo({ left: target.offsetLeft - this.cards[0].offsetLeft, behavior: !animate || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    update() {
      if (!this.cards.length) return;
      const left = this.track.getBoundingClientRect().left;
      const nearest = this.cards.reduce((best, card, index) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.cards[best].getBoundingClientRect().left - left) ? index : best, 0);
      this.index = Math.min(this.pageCount - 1, Math.round(nearest / this.step));
      if (this.previous) this.previous.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.pageCount - 1;
      this.dots.forEach((dot, index) => index === this.index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
      const template = this.mobile ? this.dataset.videoLabel : this.dataset.pageLabel;
      const text = template.replace('[current]', String(this.index + 1)).replace('[total]', String(this.pageCount));
      if (this.status.textContent !== text) this.status.textContent = text;
    }
    async open(link, url) {
      this.cleanModal();
      this.returnFocus = link;
      this.querySelector('[data-modal-title]').textContent = link.dataset.reelTitle;
      this.querySelector('[data-fallback]').href = url;
      this.dialog.dataset.embedState = 'loading';
      this.embed.setAttribute('aria-busy', 'true');
      this.embedStatus.textContent = this.dataset.loadingLabel;
      const block = document.createElement('blockquote');
      block.className = 'instagram-media';
      block.dataset.instgrmPermalink = url;
      block.dataset.instgrmVersion = '14';
      // Instagram requires a blockquote. Keep its temporary raw link out of
      // the visible UI; the separate Watch on Instagram link stays available.
      block.setAttribute('aria-hidden', 'true');
      const fallback = document.createElement('a');
      fallback.href = url;
      fallback.textContent = link.dataset.reelTitle;
      fallback.target = '_blank';
      fallback.rel = 'noopener noreferrer';
      fallback.tabIndex = -1;
      block.append(fallback);
      this.embed.append(block);
      if (!this.dialog.open) this.dialog.showModal();
      this.querySelector('[data-close]').focus();
      const token = this.modalToken;
      const current = () => token === this.modalToken && this.dialog.open;
      const ready = () => {
        if (!current()) return;
        clearTimeout(this.timer);
        this.dialog.dataset.embedState = 'ready';
        this.embed.setAttribute('aria-busy', 'false');
        this.embedStatus.textContent = '';
      };
      const unavailable = () => {
        if (!current()) return;
        this.dialog.dataset.embedState = 'error';
        this.embed.setAttribute('aria-busy', 'false');
        this.embedStatus.textContent = this.dataset.errorLabel;
      };
      this.modalAbort = new AbortController();
      let watchedFrame;
      const watchFrame = () => {
        const frame = this.embed.querySelector('iframe');
        if (!frame || frame === watchedFrame || !current()) return;
        watchedFrame = frame;
        // Iframe insertion is not readiness. Wait for its document load;
        // playback availability remains controlled by Instagram itself.
        frame.addEventListener('load', () => {
          if (frame === this.embed.querySelector('iframe')) ready();
        }, { once: true, signal: this.modalAbort.signal });
        frame.addEventListener('error', () => {
          if (frame === this.embed.querySelector('iframe')) unavailable();
        }, { once: true, signal: this.modalAbort.signal });
      };
      this.observer = new MutationObserver(watchFrame);
      this.observer.observe(this.embed, { childList: true, subtree: true });
      this.timer = setTimeout(unavailable, 20000);
      try {
        await loadInstagram();
        if (!current()) return;
        window.instgrm.Embeds.process();
        watchFrame();
      } catch (error) {
        // A network timeout can recover late. Keep the calm loading state
        // until the bounded modal timeout, while the external link is usable.
        if (error.message !== 'timeout') unavailable();
      }
    }
    cleanModal() {
      this.modalToken = (this.modalToken || 0) + 1;
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.modalAbort?.abort();
      this.embed?.replaceChildren();
      this.embed?.setAttribute('aria-busy', 'false');
      if (this.dialog) delete this.dialog.dataset.embedState;
      if (this.embedStatus) this.embedStatus.textContent = '';
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
  }
  customElements.define('ps-instagram-carousel', PSInstagramCarousel);
})();
